/**
 * Floating "Ask AI" pill. framer-motion drag (transform-based — the grab
 * point is preserved, no jump), spring snap to the nearest corner, corner
 * persisted in localStorage. Tap-vs-drag is handled by framer natively.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, animate, motion, useMotionValue } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import { HoverTip } from '@/shared/components/ui/hover-tip'

type Corner = 'br' | 'bl' | 'tr' | 'tl'

const CORNERS: Corner[] = ['tl', 'tr', 'bl', 'br']
const STORAGE_KEY = 'mcd-chat-corner'
const MARGIN = 24
const BUBBLE_H = 40

function readCorner(): Corner {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Corner | null
    if (saved && ['br', 'bl', 'tr', 'tl'].includes(saved)) return saved
  } catch {
    /* private mode */
  }
  return 'bl'
}

/**
 * Left corners align to the sidebar icon rail's horizontal center (the
 * collapsed-rail column — expanded-mode icons sit within ~8px of the same
 * axis) instead of a bare viewport margin, so the bubble reads as part of
 * the nav column. Falls back to MARGIN if the layout vars are missing
 * (e.g. mobile, where the sidebar is off-canvas).
 */
function leftRailX(width: number): number {
  const cs = getComputedStyle(document.documentElement)
  const inset = parseFloat(cs.getPropertyValue('--sidebar-inset'))
  const rail = parseFloat(cs.getPropertyValue('--sidebar-width-collapsed'))
  // The sidebar lives inside #root's `zoom: var(--content-scale)` while this
  // bubble portals to body (unzoomed), so the rail's VISUAL center is its CSS
  // center × the content scale.
  const scale = parseFloat(cs.getPropertyValue('--content-scale')) || 1
  if (Number.isFinite(inset) && Number.isFinite(rail) && rail * scale > width) {
    return Math.round((inset + rail / 2) * scale - width / 2)
  }
  return MARGIN
}

function cornerPoint(corner: Corner, width: number): { left: number; top: number } {
  return {
    left: corner[1] === 'l' ? leftRailX(width) : window.innerWidth - width - MARGIN,
    top: corner[0] === 't' ? MARGIN : window.innerHeight - BUBBLE_H - MARGIN,
  }
}

/** Corner whose landing spot is closest to the bubble's current center. */
function nearestCorner(el: HTMLElement): Corner {
  const rect = el.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  return `${cy < window.innerHeight / 2 ? 't' : 'b'}${cx < window.innerWidth / 2 ? 'l' : 'r'}` as Corner
}

export function AskAiBubble({ onOpen }: { onOpen: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [base, setBase] = useState<{ left: number; top: number } | null>(null)
  const [shieldActive, setShieldActive] = useState(false)
  // Non-null while dragging: the corner the bubble will land in on release.
  // Drives the drop-zone ghosts, and is the single source of truth for the
  // snap target so what's highlighted is always where it lands.
  const [dropTarget, setDropTarget] = useState<Corner | null>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const dragging = useRef(false)

  // Initial + resize positioning. useLayoutEffect so the REAL pill width is
  // measured before paint — the element is always rendered (hidden until
  // placed), otherwise a remount (drawer close) would fall back to a guessed
  // width and miss the corner.
  useLayoutEffect(() => {
    const place = () => {
      const width = ref.current?.offsetWidth ?? 120
      setBase(cornerPoint(readCorner(), width))
      x.set(0)
      y.set(0)
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [x, y])

  const settleIntoCorner = (corner: Corner) => {
    const el = ref.current
    if (!el || !base) return
    try {
      localStorage.setItem(STORAGE_KEY, corner)
    } catch {
      /* private mode */
    }
    // offsetWidth = layout width, unaffected by the hover/tap scale transform
    const target = cornerPoint(corner, el.offsetWidth)
    // Rebase so the switch is visually seamless, then glide the offset to 0.
    // Ease-out tween (no spring bounce) — the drop ghost already marks the
    // exact landing spot, so the bubble should settle onto it precisely.
    const dx = base.left + x.get() - target.left
    const dy = base.top + y.get() - target.top
    setBase(target)
    x.set(dx)
    y.set(dy)
    animate(x, 0, { duration: 0.22, ease: [0, 0, 0.2, 1] })
    animate(y, 0, { duration: 0.22, ease: [0, 0, 0.2, 1] })
  }

  return (
    <>
      {/* Drag shield + scrim: while dragging it keeps pointermove events on
          THIS document (without it the drag freezes whenever the cursor
          crosses an iframe, e.g. the chart PDF viewer) AND dims the page so
          the corner drop zones read clearly against any content. */}
      <AnimatePresence>
        {shieldActive && (
          <motion.div
            key="drag-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0, 0, 0.2, 1] }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 8998,
              cursor: 'grabbing',
              userSelect: 'none',
              background: 'var(--color-bg-overlay, rgba(11, 19, 36, 0.45))',
            }}
          />
        )}
      </AnimatePresence>
      {/* Drop zones: while dragging, ghost the 4 corner landing spots so the
          user can aim. The nearest corner (= where release will land) glows. */}
      {dropTarget &&
        CORNERS.map((corner) => {
          const w = ref.current?.offsetWidth ?? BUBBLE_H
          const p = cornerPoint(corner, w)
          const active = corner === dropTarget
          return (
            <motion.div
              key={corner}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: active ? 1 : 0.5, scale: active ? 1.5 : 1 }}
              transition={{ duration: 0.16, ease: [0, 0, 0.2, 1] }}
              style={{
                position: 'fixed',
                left: p.left,
                top: p.top,
                width: w,
                height: BUBBLE_H,
                borderRadius: 'var(--r-full, 9999px)',
                zIndex: 8999,
                pointerEvents: 'none',
                // Styled to read against the dark drag scrim behind them
                border: active ? '2px solid rgba(255, 255, 255, 0.9)' : '1.5px dashed rgba(255, 255, 255, 0.65)',
                background: active ? 'var(--askai-gradient, #2454c0)' : 'rgba(255, 255, 255, 0.22)',
                boxShadow: active ? '0 0 0 8px rgba(255, 255, 255, 0.18), 0 6px 20px rgba(36, 84, 192, 0.35)' : 'none',
              }}
            />
          )
        })}
      <motion.div
        ref={ref}
        drag
        dragMomentum={false}
        dragElastic={0.08}
        onDragStart={() => {
          dragging.current = true
          setShieldActive(true)
          if (ref.current) setDropTarget(nearestCorner(ref.current))
        }}
        onDrag={() => {
          if (ref.current) {
            const next = nearestCorner(ref.current)
            setDropTarget((prev) => (prev === next ? prev : next))
          }
        }}
        onDragEnd={() => {
          setShieldActive(false)
          settleIntoCorner(dropTarget ?? (ref.current ? nearestCorner(ref.current) : 'bl'))
          setDropTarget(null)
          // Let the click that follows pointerup be ignored
          setTimeout(() => {
            dragging.current = false
          }, 0)
        }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        style={{
          position: 'fixed',
          zIndex: 9000,
          left: base?.left ?? -9999,
          top: base?.top ?? -9999,
          visibility: base ? 'visible' : 'hidden',
          x,
          y,
          touchAction: 'none',
        }}
      >
        <HoverTip label="Ask AI" side="top" bubbleMinWidth={0} disabled={shieldActive}>
          <button
            type="button"
            aria-label="Open MediCodio AI assistant"
            onClick={() => {
              if (!dragging.current) onOpen()
            }}
            style={{
              height: BUBBLE_H,
              width: BUBBLE_H,
              padding: 0,
              borderRadius: 'var(--r-full, 9999px)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'var(--askai-gradient)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              boxShadow: 'var(--askai-shadow, 0 6px 20px rgba(36, 84, 192, 0.30))',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <Sparkles size={18} strokeWidth={2.2} style={{ flexShrink: 0 }} />
          </button>
        </HoverTip>
      </motion.div>
    </>
  )
}
